<template>
  <div class="base-form">
    <page-form
      ref="baseFormRef"
      v-bind="configOptions"
      @handleNewClick="handleSubmit"
      :pageInfo="pageInfo"
    >
    </page-form>
    <page-content
      v-if="pageType === 'view'"
      style="margin-top: 10px"
      ref="pageContentRef"
      v-bind="tableOptionsConfig"
      @newBtnClick="handleNew"
      @pageInfoChange="pageInfoChangeAction"
    >
      <template #otherHandler="scope">
        <el-button
          v-if="isNormalStatus(scope.row.status)"
          class="opt-btn"
          icon="el-icon-lock"
          size="mini"
          plain
          type="warning"
          @click="handleOperationClick(scope.row, 'disable')"
          >{{ $t('general.disable') }}</el-button
        >
        <el-button
          v-if="!isNormalStatus(scope.row.status)"
          class="opt-btn"
          icon="el-icon-unlock"
          size="mini"
          plain
          @click="handleOperationClick(scope.row, 'enable')"
          >{{ $t('general.enable') }}</el-button
        >
      </template>
    </page-content>
  </div>
</template>

<script setup>
import pageForm from '@/components/PageForm/page-form.vue'
import pageContent from '@/components/PageContent/page-content.vue'

import {
  baseNewFormConfig,
  baseUpdateFormConfig,
  baseViewFormConfig
} from './config/index'
import { contentTableConfig } from './config/table.js'

import { computed, ref, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import i18n from '@/i18n'
// import { usePermission } from '@/hooks/use-permission'

const routes = useRoute()
const router = useRouter()

const { t } = i18n.global
const pageContentRef = ref('')

const id = routes.params.id
const pageInitialParams = {
  id: id
}

// 表格基础配置项
const tableOptions = {
  moduleName: 'file', // 所属的模块
  pageName: 'applicationFile', // 所属的页面
  contentTableConfig, // 表格列的基础配置
  isShowCreateSlot: true,
  isShowViewBtn: false,
  isShowEditBtn: false,
  pageInitialQuery: pageInitialParams,
  pageAction: 'file/getApplicationFileLists', // 获取表格的Action
  deleteRecordAction: 'file/deleteApplicationFile', // 删除record的Action
  pageCountGetter: 'file/pageListCount', // 获取表格记录数据总和getters
  pageListGetter: 'file/pageListData' // 获取表格列表数据getters
}

const tableOptionsConfig = computed(() => tableOptions)

// 1、预定义当前页面的map映射数组
const pageMapArray = [
  {
    // view routesName 跟你配置路由的name相关
    routesName: 'ApplicationView',
    baseFormConfig: baseViewFormConfig,
    pageTitle: t('general.router-application-view'),
    pageType: 'view'
  },
  {
    routesName: 'ApplicationEdit',
    baseFormConfig: baseUpdateFormConfig,
    pageTitle: t('general.router-application-edit'),
    pageType: 'edit',
    // edit page特有的属性 reset时不将表单置为空
    isResetFields: false
  },
  {
    routesName: 'ApplicationNew',
    baseFormConfig: baseNewFormConfig,
    pageTitle: t('general.router-application-new'),
    pageType: 'add'
  }
]

// 2、根据当前路由对象中的name属性判断当前页面
const currentPageConfig = pageMapArray.find((item) => {
  if (item.routesName === routes.name) return item
})

const store = useStore()
// 3、将需要用到的属性解构出来
const { baseFormConfig, pageType } = currentPageConfig

const baseFormConfigRef = computed(() => {
  return baseFormConfig
})

// 4、区分当前页面是否需要转化options选项
currentPageConfig.baseFormConfig = baseFormConfigRef
const configOptions = ref({})
configOptions.value = currentPageConfig

const { appContext } = getCurrentInstance()
const pageInfo = ref({})

const baseFormRef = ref('')

const handleMessageCommit =
  appContext.config.globalProperties.$handleMessageCommit

// 获取页面操作的权限
// const isDisable = usePermission(
//   tableOptions.moduleName,
//   tableOptions.pageName,
//   'switch'
// )

const isNormalStatus = (status) => {
  return status === '00'
}

// 5、定义方法获取当前页面的初始化数据
const currentApplicationType = ref('')
const getCurrentPageInfo = async () => {
  // 获取当前页面的初始化值
  const application = await store.dispatch('file/handleGetApplication', {
    id
  })
  currentApplicationType.value = application?.appType
  application.tenantWhitelist = application.tenantWhitelist?.split(',') || []
  application.tenantId = store.getters.tenantDataMap[application?.tenantId]
  pageInfo.value = application
}

// 6、判断当前页是否需要调用getCurrentPageInfo
if (['view', 'edit'].includes(pageType)) {
  getCurrentPageInfo()
} else {
  pageInfo.value = {}
}

// 7、定义被提交的方法
const handleSubmit = async (data) => {
  let params = { ...data }

  params.screenShotUrl = Array.isArray(data.screenShotUrl)
    ? data?.screenShotUrl
        ?.map((item) => {
          return item.url
        })
        ?.join(',')
    : ''

  params.tenantWhitelist = Array.isArray(data.tenantWhitelist)
    ? data.tenantWhitelist?.join(',')
    : ''
  // 获取当前的页面的ICON的值

  if (pageType === 'add') {
    params = { id: '', ...params }
    handleMessageCommit('file/handleApplicationAction', params).then(() => {
      router.push('/file/app')
    })
  } else if (pageType === 'edit') {
    params = { id, ...params }
    handleMessageCommit('file/handleApplicationEdit', params).then(() => {
      router.push('/file/app')
    })
  }
}

const handleNew = () => {
  router.push(`/file/app/import/${currentApplicationType.value}`)
}

const handleOperationClick = async (row, type) => {
  const urlMap = {
    enable: 'handleEnableApplicationAction',
    disable: 'handleDisableApplicationAction',
    push: 'handlePushApplicationAction'
  }
  handleMessageCommit(`file/${urlMap[type]}`, row.id).then(() => {
    pageContentRef.value.getPageData({ id })
  })
}

const pageInfoChangeAction = () => {
  pageContentRef.value.getPageData({ id })
}
</script>

<style scoped lang="scss">
.base-form {
  .form-item {
    padding: 5px 20px;
    .file-wrap {
      display: flex;
      align-items: center;
      &:hover {
        cursor: pointer;
        color: #64b5fc;
      }
      .download-icon {
        font-size: 16px;
        margin-left: 5px;
      }
    }
  }
}
</style>
